import template from './report_leave_trends.html';
import * as Utils from '../../lib/savanna_utils.js';
import tr from '../../lib/default-translator.js';

class ReportDepartmentGrowthViewModel
{
	constructor (page)
	{
		this.page = page;
		this.loader = ko.observable(false);
		this.show_chart = ko.observable(false);

		let first_day = moment().subtract(7, 'months').format('YYYY-MM-') + moment().subtract(7, 'months').daysInMonth();
		let last_day = moment().format('YYYY-MM-') + moment().daysInMonth();

		this.date_start = ko.observable(new Date(first_day).toJSON().slice(0, 10));
		this.date_end = ko.observable(new Date(last_day).toJSON().slice(0, 10));

		this.current_department_id = ko.observable();
		this.selected_department = ko.observable();

		this.refresh_button = ko.observable(false);

		this.total_employed_in_company = ko.observable(0);
		this.total_resigned_in_company = ko.observable(0);
		this.company_growth_percentage = ko.observable(0);

		this.leave_types = ko.observableArray();
		this.selected_leave_type = ko.observable();
		this.selected_leave_types = ko.observableArray();

		this.reportData = ko.observableArray();
		this.reportAdjacentLeaveData = ko.observableArray();

		this.FocusPersonDataArray = ko.observableArray();

		this.selected_graph_options = ko.observableArray();

		// Remove automatic updates on date changes
		this.date_start.subscribe((newValue) => {
			if (!newValue) return;

			let sd = moment(newValue, 'YYYY-MM-DD', true);
			if (!sd.isValid()) return;

			// Update end date to last day of start date month
			let month_end = moment(sd).endOf('month').format('YYYY-MM-DD');
			this.date_end(month_end);
		});

		this.date_end.subscribe((newValue) => {
			if (!newValue) return;

			let ed = moment(newValue, 'YYYY-MM-DD', true);
			if (!ed.isValid()) return;

			if (newValue.length === 10)
			{
				let sd = moment(this.date_start(), 'YYYY-MM-DD', true);
				if (sd.isValid() && ed.isBefore(sd))
				{
					Grape.alerts.alert({
						type: 'error',
						title: tr('Invalid Date Range'),
						message: tr('End date cannot be before start date')
					});
				}
			}
		});
		
		// Remove automatic update on leave types selection
		this.selected_leave_types.subscribe((newValue) => {
			// Just update the refresh button state if needed
			this.refresh_button(true);
		});

		this.selected_options_text = ko.computed(() => {
			let selected_ids = this.selected_leave_types();
			if (selected_ids.length === 0) return 'View leave type options';
			
			if (selected_ids.length > 3) return `${selected_ids.length} leave types selected`;
		
			let selected_desc = this.leave_types().filter(option => selected_ids.includes(option.leave_type_id)).map(option => option.description);
			return selected_desc.join(', ');
		});
		
		// Remove automatic update on department change
		this.selected_department.subscribe((newValue) => {
			if (newValue && newValue.department_id)
			{
				this.current_department_id(newValue.department_id);
			}
			else
			{
				this.current_department_id(null);
			}
		});
	}

	show_report_help()
	{
		Grape.dialog.open('LeaveTrendsHelp', { employee_id: this.page.bindings.id });
	}

	graph_option_click (row)
	{
		let selected_options = this.selected_leave_types();

		if (selected_options.indexOf(row.leave_type_id) == -1)
			this.selected_leave_types.push(row.leave_type_id);
		else
			this.selected_leave_types.remove(row.leave_type_id);

		this.refresh_button(true);
		return true;
	}
}

class ReportDepartmentGrowthPage
{
	constructor (bindings)
	{
		this.bindings = bindings;
		this.viewModel = new ReportDepartmentGrowthViewModel(this);
		this.chart = null;
		this.leave_types_ticked_filter = [];
		this.leave_trend_res = null;
	}

	async init()
	{
		// check access to page
		if (await Utils.redirectOnAccessDenied('LeaveTrendsReport'))
			return;

		document.title = tr('Leave Trends Report');
		window.current_page_title(tr('Leave Trends'));

		let res = await Grape.fetches.getJSON('/api/record', { table: 'leave_type', limit: 100 });
		
		// Sort leave types alphabetically by description
		let sorted_leave_types = res.records.sort((a, b) => {
			return a.description.localeCompare(b.description);
		});
		
		this.viewModel.leave_types(sorted_leave_types);

		this.setupGraph();
		// Remove automatic updateData call on init
	}

	async updateData()
	{
		if (
			this.viewModel.date_start() != '' &&
			this.viewModel.date_start() != undefined &&
			this.viewModel.date_end() != '' &&
			this.viewModel.date_end() != undefined
		)
		{

			let data = {
				date_start: this.viewModel.date_start(),
				date_end: this.viewModel.date_end(),
				leave_types: this.viewModel.selected_leave_types(),
				department_id: this.viewModel.current_department_id()
			};

			this.viewModel.loader(true);
			this.viewModel.FocusPersonDataArray.removeAll()

			if(this.viewModel.current_department_id() == null)
			{
				this.viewModel.loader(false);
				return;
			}

			let res = await Grape.fetches.postJSON('/reports/leave_trends', data);

			this.leave_trend_res = res;

			if (res.status == 'OK')
			{
				this.viewModel.show_chart(true);
				this.buildGraph(res);

				if (res.leave_trends.person != null)
					this.viewModel.reportData(res.leave_trends.person);
				else
					this.viewModel.reportData.removeAll();

				if (res.leave_trends.adjacent_leave_taken != null)
					this.viewModel.reportAdjacentLeaveData(res.leave_trends.adjacent_leave_taken);
				else
					this.viewModel.reportAdjacentLeaveData.removeAll();
			}
			else if (res.status == 'ERROR')
				Grape.alerts.apiError(res);
			else
				Grape.alerts.alert({ type: 'error', message: tr('Something went wrong...'), title: tr('Error') });
				
			this.viewModel.loader(false);
		}
	}

	setupGraph()
	{
		this.chart = c3.generate({
			bindto: '#chart',
			size: {
				height: 400
			},
			data: {
				x: 'x',
				columns: [
					/* ['x', 'Platsoft', 'Skinchem', 'Maths', 'IG' , 'QC' , 'Platlife' ],
					['employed', 30, 200, 100, 400, 150, 250],
					['resignations', 130, 100, 140, 200, 150, 50] */
				],
				colors: {
					'employed': '#31859D',
					'resignations': '#FF0000',
					'growth': '#FFF000'
				},
				type: 'bar'
			},
			transition: {
				duration: 1000
			},
			legend: {
				show: false
			},
			bar: {
				width: {
					ratio: 0.7 // this makes bar width 50% of length between ticks
				}
			},
			axis: {
				x: {
					type: 'category' // this needed to load string
				}
			}
		});
	}

	buildGraph(res)
	{
		if (res.leave_trends != null)
		{
			// reset
			this.viewModel.total_employed_in_company(0);
			this.viewModel.total_resigned_in_company(0);
			this.viewModel.company_growth_percentage(0);

			let labelArray = [
				'x', tr('Monday'),
				tr('Tuesday'),
				tr('Wednesday'),
				tr('Thursday'),
				tr('Friday')
			];
			let dataArray = [];
			let dataArrayAverages = [tr('Department average days')];
			let dataArrayPerson = [];

			let total_employed = 0;
			let total_resigned = 0;
			let total_before_start_date = 0;

			if (this.viewModel.FocusPersonDataArray().length > 0)
				dataArrayPerson = this.viewModel.FocusPersonDataArray();

			// build the averages
			// for ( var a = 0 ; a < res.leave_trends.averages.length ; a++ ){
			dataArrayAverages.push(res.leave_trends.averages.avg_monday);
			dataArrayAverages.push(res.leave_trends.averages.avg_tuesday);
			dataArrayAverages.push(res.leave_trends.averages.avg_wednesday);
			dataArrayAverages.push(res.leave_trends.averages.avg_thursday);
			dataArrayAverages.push(res.leave_trends.averages.avg_friday);
			// }

			/* for ( var a = 0 ; a < res.length ; a++ ){

				if ( res[a].employed > 0 || res[a].resignations > 0 ) {
					var people = res[a].employed_before	+ res[a].employed + res[a].resignations;
					var growth = (( (res[a].employed  - res[a].resignations) /people ) * 100).toFixed(2);
					labelArray.push(res[a].department + ' [Growth "+growth+"%]');
					dataArrayAverages.push( res[a].employed );
					dataArrayResigned.push( res[a].resignations );
				}
					total_employed += res[a].employed;
					total_resigned += res[a].resignations;
					total_before_start_date += res[a].employed_before;
			} */

			// this.viewModel.total_employed_in_company(total_employed);
			// this.viewModel.total_resigned_in_company(total_resigned);
			// var total_people = total_before_start_date + total_employed + total_resigned;
			//	var growth = (( (total_employed - total_resigned) /total_people ) * 100).toFixed(2) + ' %';
			// this.viewModel.company_growth_percentage(growth);

			this.chart.unload(); // clear chart

			setTimeout(() => {
				this.chart.load({
					columns: [
						labelArray,
						dataArrayAverages,
						dataArrayPerson

					]
				});
			}, 1100);
		}
		else
			this.chart.unload(); // clear chart
	}

	view_on_graph(v, e)
	{
		let data = ko.toJS(v);

		let row = e.currentTarget.closest('tr');

		document.querySelectorAll('#leave_taken_report tbody tr').forEach(tr => {
			tr.classList.remove('report_focusview');
		});

		row.classList.add('report_focusview');

		this.viewModel.FocusPersonDataArray.removeAll();
		this.viewModel.FocusPersonDataArray.push(data.firstname + ' ' + data.surname);
		this.viewModel.FocusPersonDataArray.push(data.monday);
		this.viewModel.FocusPersonDataArray.push(data.tuesday);
		this.viewModel.FocusPersonDataArray.push(data.wednesday);
		this.viewModel.FocusPersonDataArray.push(data.thursday);
		this.viewModel.FocusPersonDataArray.push(data.friday);

		this.buildGraph(this.leave_trend_res);
	}
}

export default {
	route: '[/]report/leave_trends',
	page_class: ReportDepartmentGrowthPage,
	template: template,
	template_filename: 'translation/report_leave_trends'
};